export enum Captura {
  Aditum = 'Aditum',
  Phoebus = 'Phoebus',
}

export enum ModalidadePhoebus {
  DebitoCredito = 'DEBITO_CREDITO',
  Pix = 'PIX',
}

export interface IReprocessTransactionRequest {
  capture: Captura;
  startDate: string; 
  endDate: string; 
  merchant?: string | null; 
  acquirerNsu?: string | null;
}

export interface IReprocessFailure {
  uuid: string | null;
  merchant: string | null;
  reason: string;
}

export interface IReprocessResult {
  totalFound: number;
  sent: number;
  failures: IReprocessFailure[];
  warnings: string[];
}

/**
 * Formato assíncrono (HTTP 202) devolvido pelo reprocessamento de
 * crédito/débito. Idêntico ao do PIX (IReprocessPixAccepted); mantido como
 * alias para deixar explícito o uso no fluxo de transação.
 */
export type IReprocessTransactionAccepted = IReprocessPixAccepted;

/**
 * O reprocessamento de transação pode responder de duas formas:
 *  - síncrona (legado): IReprocessResult com totalFound/sent/failures;
 *  - assíncrona (202): IReprocessTransactionAccepted com jobId.
 * O front trata as duas para nunca quebrar.
 */
export type IReprocessTransactionResponse =
  | IReprocessResult
  | IReprocessTransactionAccepted;

/**
 * Discrimina qual endpoint de status consultar. Só há dois: PIX e
 * transaction (esta última cobre crédito/débito de Phoebus e Aditum).
 */
export enum JobType {
  Pix = 'pix',
  Transaction = 'transaction',
}

/**
 * Deriva o tipo de job a partir da Captura/Modalidade selecionadas no filtro.
 * Só é PIX quando a captura é Phoebus e a modalidade é Pix; todo o resto
 * (Phoebus Débito/Crédito e Aditum) é reprocessamento de transação.
 *
 * Como a decisão vem dos filtros — e não de quem disparou o job — a consulta
 * por JobId funciona mesmo com um id gerado por outra pessoa: basta o usuário
 * deixar Captura/Modalidade coerentes com o job que quer consultar.
 */
export const resolveJobType = (
  captura: Captura,
  modalidade: ModalidadePhoebus,
): JobType =>
  captura === Captura.Phoebus && modalidade === ModalidadePhoebus.Pix
    ? JobType.Pix
    : JobType.Transaction;

/** True quando a resposta é o formato assíncrono (202) com jobId. */
export const isAcceptedResponse = (
  response: IReprocessTransactionResponse,
): response is IReprocessTransactionAccepted =>
  (response as IReprocessTransactionAccepted)?.jobId !== undefined;

export interface IReprocessPixRequest {
  initDate: string; 
  finishDate: string;
  merchantId?: string | null; 
  appPixId?: string | null;
  endToEndId?: string | null;
}

export interface IReprocessPixAccepted {
  message: string;
  jobId: string;
  status: string; // "STARTED"
  startedAt: string;
  statusUrl: string;
}

export enum EnumExecutionStatus {
  Started = 'STARTED',
  Concluded = 'CONCLUDED',
  Failed = 'FAILED',
}

export interface IPixJobStatusFilters {
  initDate: string | null;
  finishDate: string | null;
  merchantId: string | null;
  appPixId: string | null;
  endToEndId: string | null;
  partnerName: string | null;
}

/**
 * Relatório do reprocessamento de transação (crédito/débito). O backend de
 * transação aninha os totais em `result`, ao contrário do PIX que expõe
 * `processedCount` na raiz. Normalizamos em consultarStatus.
 */
export interface IReprocessTransactionStatusResult {
  totalFound: number;
  sent: number;
  failures: IReprocessFailure[];
  warnings: string[];
}

export interface IPixJobStatus {
  jobId: string;
  status: EnumExecutionStatus;
  isRunning: boolean;
  isConcluded: boolean;
  isFailed: boolean;
  startedAt: string;
  finishedAt: string | null;
  elapsedSeconds: number | null;
  /**
   * Quantidade sincronizada com sucesso. No PIX vem direto do backend; no
   * fluxo de transação é normalizado a partir de `result.sent`.
   */
  processedCount: number | null;
  errorMessage: string | null;
  filters: IPixJobStatusFilters;
  statusUrl: string;
  /**
   * Relatório detalhado do fluxo de transação (totalFound/sent/failures/
   * warnings). Ausente no fluxo PIX.
   */
  result?: IReprocessTransactionStatusResult | null;
}
